"use client";

import {
  SidebarPreferences,
  writeSidebarPreferencesCookie,
} from "@/lib/sidebar-preferences";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

type SidebarPreferencesContextValue = {
  preferences: SidebarPreferences;
  setOpen: (open: boolean) => void;
  setCategoryOpen: (title: string, open: boolean) => void;
};

const SidebarPreferencesContext =
  createContext<SidebarPreferencesContextValue | null>(null);

export const SidebarPreferencesProvider = ({
  initialPreferences,
  children,
}: {
  initialPreferences: SidebarPreferences;
  children: React.ReactNode;
}) => {
  const [preferences, setPreferences] = useState(initialPreferences);

  const setOpen = useCallback((open: boolean) => {
    setPreferences((current) => {
      const next = { ...current, open };
      writeSidebarPreferencesCookie(next);
      return next;
    });
  }, []);

  const setCategoryOpen = useCallback((title: string, open: boolean) => {
    setPreferences((current) => {
      const next = {
        ...current,
        categories: {
          ...current.categories,
          [title]: open,
        },
      };
      writeSidebarPreferencesCookie(next);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      preferences,
      setOpen,
      setCategoryOpen,
    }),
    [preferences, setOpen, setCategoryOpen],
  );

  return (
    <SidebarPreferencesContext.Provider value={value}>
      {children}
    </SidebarPreferencesContext.Provider>
  );
};

export const useSidebarPreferences = () => {
  const context = useContext(SidebarPreferencesContext);

  if (!context) {
    throw new Error(
      "useSidebarPreferences must be used within SidebarPreferencesProvider.",
    );
  }

  return context;
};
